import { useFieldContext } from '@components/ui/form/form';
import { FormDescription, FormErrorMessage, FormField, FormLabel } from '@components/ui/form/shared.component';
import { Input } from '@components/ui/input';
import { errorToast } from '@hooks/toast.hook';
import { cn } from '@lib/utils';
import { UploadIcon } from 'lucide-react';
import { type ChangeEvent, type FC, type PropsWithChildren, useEffect, useRef, useState } from 'react';
import {
  TECHNOLOGY_IMAGE_ACCEPT,
  TECHNOLOGY_IMAGE_FIELD_DESCRIPTION,
  validateTechnologyImageFile,
} from './technology-image.util';
import { useImageAttachmentDrop } from './use-image-attachment-drop';

type ImageAttachmentFieldProps = PropsWithChildren<{
  existingImageUrl?: string;
  fileDisplayName: string;
  isPending: boolean;
  showReadOnlyField: boolean;
}>;

export const ImageAttachmentField: FC<ImageAttachmentFieldProps> = (props) => {
  const { children, existingImageUrl, fileDisplayName, isPending, showReadOnlyField } = props;
  const field = useFieldContext<File | undefined>();
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(existingImageUrl);
  const inputRef = useRef<HTMLInputElement>(null);
  const currentFileDisplayName =
    field.state.value?.name ?? (fileDisplayName === '-' ? 'No image selected' : fileDisplayName);

  useEffect(() => {
    if (!field.state.value) {
      setPreviewUrl(existingImageUrl);
      return;
    }

    const nextPreviewUrl = URL.createObjectURL(field.state.value);
    setPreviewUrl(nextPreviewUrl);

    return () => {
      URL.revokeObjectURL(nextPreviewUrl);
    };
  }, [existingImageUrl, field.state.value]);

  useEffect(() => {
    if (field.state.value || !inputRef.current) {
      return;
    }

    inputRef.current.value = '';
  }, [field.state.value]);

  const openFilePicker = (): void => {
    if (showReadOnlyField || isPending) {
      return;
    }

    inputRef.current?.click();
  };

  const syncInputFile = (file?: File): void => {
    if (!inputRef.current) {
      return;
    }

    if (!file) {
      inputRef.current.value = '';
      return;
    }

    if (typeof DataTransfer === 'undefined') {
      return;
    }

    const dataTransfer = new DataTransfer();
    dataTransfer.items.add(file);
    inputRef.current.files = dataTransfer.files;
  };

  const handleFileChange = (params: {
    file?: File;
    syncInput: boolean;
  }): void => {
    const { file, syncInput } = params;

    if (syncInput) {
      syncInputFile(file);
    }

    field.handleChange(file);
  };

  const { handleDragLeave, handleDragOver, handleDrop, isDragOver } = useImageAttachmentDrop({
    disabled: isPending || showReadOnlyField,
    onError: (description) => errorToast({ description }),
    onFileSelect: (file) =>
      handleFileChange({
        file,
        syncInput: true,
      }),
    validateFile: validateTechnologyImageFile,
  });

  return (
    <div className="grid gap-4 md:grid-cols-[10rem_minmax(0,1fr)] md:items-start">
      <div className="flex w-full max-w-40 shrink-0 flex-col gap-2">
        <button
          className={cn(
            'group relative flex size-40 items-center justify-center overflow-hidden rounded-2xl border border-gray-200 bg-gray-50 transition-colors',
            isDragOver && !showReadOnlyField ? 'border-blue-400 bg-blue-50' : undefined,
            showReadOnlyField ? 'cursor-default' : 'cursor-pointer hover:bg-gray-100',
          )}
          disabled={isPending || showReadOnlyField}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          type="button"
          onClick={openFilePicker}
        >
          {previewUrl ? (
            <img
              alt="Attachment preview"
              className="size-full object-cover"
              src={previewUrl}
            />
          ) : (
            <div className="flex flex-col items-center gap-1 text-gray-500">
              <UploadIcon className="size-5" />
              <span className="text-xs font-medium">Add image</span>
            </div>
          )}
        </button>
      </div>

      <div className="flex flex-col gap-4">
        {children}
        {showReadOnlyField ? (
          <FormField>
            <FormLabel>Image</FormLabel>
            <div
              aria-disabled={true}
              className="h-10 w-full cursor-not-allowed truncate rounded-md border border-gray-200 bg-gray-100 px-3 py-2 text-sm text-gray-500 opacity-70"
              title={currentFileDisplayName}
            >
              {currentFileDisplayName}
            </div>
            <FormDescription>To replace this image, delete the attachment and add a new one.</FormDescription>
          </FormField>
        ) : (
          <FormField>
            <FormLabel htmlFor={field.name}>Image</FormLabel>
            <Input
              accept={TECHNOLOGY_IMAGE_ACCEPT}
              aria-invalid={!field.state.meta.isValid}
              className="sr-only"
              hasError={!field.state.meta.isValid}
              id={field.name}
              name={field.name}
              ref={inputRef}
              onBlur={field.handleBlur}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                const file = event.target.files?.[0];

                handleFileChange({
                  file,
                  syncInput: false,
                });
              }}
              type="file"
            />
            <FormDescription>{TECHNOLOGY_IMAGE_FIELD_DESCRIPTION}</FormDescription>
            <div
              className={cn(
                'flex h-10 w-full items-center rounded-md border bg-gray-100 text-base text-gray-600 md:text-sm',
                field.state.meta.isValid ? 'border-gray-200' : 'border-red-500',
              )}
            >
              <button
                className="ml-1.5 mr-2 inline-flex h-6.5 shrink-0 items-center rounded-md border border-gray-300 bg-gray-200 px-3 text-xs font-normal text-gray-500 transition-colors hover:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={isPending}
                type="button"
                onClick={openFilePicker}
              >
                Choose File
              </button>
              <div
                className="min-w-0 flex-1 truncate pr-3 text-sm text-gray-600"
                title={currentFileDisplayName}
              >
                {currentFileDisplayName}
              </div>
            </div>
            <FormErrorMessage />
          </FormField>
        )}
      </div>
    </div>
  );
};
